'use client'
import Link from 'next/link'
import React, { useState } from 'react'

import { motion } from 'framer-motion'
import { Clock, Globe, Loader2, Mail, MapPin, Phone, Rocket, Send, Users } from 'lucide-react'

import { getContactApiUrl } from '../../lib/lead-api'
import { CLOUD_SHUTTLE_HQ_LABEL } from '../../lib/site-organization'
import { cn } from '../../lib/utils'
import { Card, CardContent } from '../card'
import { CornerBrackets } from '../kinetic/CornerBrackets'
import { KineticAboutContactHero } from '../kinetic/KineticAboutContactHero'
import { Container } from '../layout/container'
import { Section } from '../layout/section'
import { Button } from '../ui/button'

interface ContactFormProps {
  color?: string
}

const contactInfo = [
  {
    icon: Mail,
    title: 'Email Us',
    details: 'info@cloudshuttle.com.au',
    description: 'Get in touch via email',
    action: 'mailto:info@cloudshuttle.com.au',
  },
  {
    icon: Phone,
    title: 'Call Us',
    details: '(+61) 433 825 293',
    description: 'Speak with our team',
    action: 'tel:+61433825293',
  },
  {
    icon: MapPin,
    title: 'Visit Us',
    details: CLOUD_SHUTTLE_HQ_LABEL,
    description: 'Headquarters',
    action: null,
  },
  {
    icon: Clock,
    title: 'Business Hours',
    details: 'Mon-Fri: 9AM-6PM AEST',
    description: "When we're available",
    action: null,
  },
]

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
    },
  },
}

interface FormErrors {
  name?: string
  email?: string
  phone?: string
  service?: string
  description?: string
}

const darkTheme = {
  bg: 'bg-kinetic-surface',
  surface: 'bg-kinetic-surface-container-low',
  surfaceBorder: 'border-kinetic-outline-variant/30',
  text: 'text-kinetic-on-surface',
  textMuted: 'text-kinetic-on-surface-variant',
  textDim: 'text-kinetic-outline',
  accent: 'text-kinetic-primary-container',
  inputBg:
    'rounded-none border-kinetic-outline-variant/25 bg-kinetic-surface-container-highest text-kinetic-on-surface placeholder:text-kinetic-outline focus:border-kinetic-primary-container focus:ring-2 focus:ring-kinetic-primary-container/25 focus:ring-offset-0',
  inputError: 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
  cardBg: 'bg-kinetic-surface-container-low border border-kinetic-outline-variant/30',
  iconBg: 'bg-kinetic-primary-container',
}

export const ContactForm = ({ color = 'default' }: ContactFormProps) => {
  const isDark = color === 'dark'
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null
    message: string
  }>({ type: null, message: '' })
  const [formErrors, setFormErrors] = useState<FormErrors>({})

  const API_ENDPOINT = getContactApiUrl()

  const validateForm = (formData: FormData): FormErrors => {
    const errors: FormErrors = {}

    const name = formData.get('name') as string
    const email = formData.get('email') as string
    const phone = formData.get('phone') as string
    const service = formData.get('service') as string
    const description = formData.get('description') as string

    if (!name?.trim()) {
      errors.name = 'Full name is required'
    } else if (name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters'
    }

    if (!email?.trim()) {
      errors.email = 'Email address is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address'
    }

    if (!phone?.trim()) {
      errors.phone = 'Phone number is required'
    } else if (!/^[\+\d\s\(\)\-]{8,}$/.test(phone.replace(/\s/g, ''))) {
      errors.phone = 'Please enter a valid phone number'
    }

    if (!service) {
      errors.service = 'Please select a service'
    }

    if (!description?.trim()) {
      errors.description = 'Project description is required'
    } else if (description.trim().length < 10) {
      errors.description = 'Description must be at least 10 characters'
    }

    return errors
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)

    // Validate form
    const errors = validateForm(formData)
    setFormErrors(errors)

    if (Object.keys(errors).length > 0) {
      setSubmitStatus({
        type: 'error',
        message: 'Please correct the errors below and try again.',
      })
      return
    }

    setIsSubmitting(true)
    setSubmitStatus({ type: null, message: '' })

    const formValues = Object.fromEntries(formData.entries())

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Origin: window.location.origin,
        },
        body: JSON.stringify(formValues),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      await response.json()
      setSubmitStatus({
        type: 'success',
        message: "Thank you for your message. We'll be in touch shortly!",
      })
      setFormErrors({})
      e.currentTarget?.reset()
    } catch (error) {
      console.error('Submission error:', error)
      setSubmitStatus({
        type: 'error',
        message: 'Something went wrong. Please try again later.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className={`min-h-screen ${isDark ? darkTheme.bg : 'bg-gradient-to-br from-shuttle-navy via-shuttle-purple to-shuttle-navy'}`}
    >
      {/* Hero Section */}
      {isDark ? (
        <KineticAboutContactHero
          statusLabel="CONTACT // ANZ"
          beforeTitle={
            <div className="mb-8 flex justify-center">
              <div className="bg-kinetic-surface-container-high/40 inline-flex items-center gap-3 border-l-2 border-kinetic-primary-container px-4 py-1.5">
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-kinetic-primary-container">
                  Cloud Shuttle
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-kinetic-secondary-fixed-dim">
                  consultancy
                </span>
              </div>
            </div>
          }
          title={
            <>
              Get in <span className="text-kinetic-primary-container">touch</span>
            </>
          }
          subtitle="Tell us about your project or workshop enquiry. We usually reply within one business day."
          afterSubtitle={
            <div className="mt-10 font-mono text-[11px] uppercase tracking-[0.2em] text-kinetic-outline">
              <div>
                HQ <span className="text-kinetic-primary-container">·</span>{' '}
                {CLOUD_SHUTTLE_HQ_LABEL}
              </div>
              <div className="mt-2">
                Direct_Line <span className="text-kinetic-primary-container">·</span> +61 433 825
                293
              </div>
              <div className="mt-2">
                Comms_Channel <span className="text-kinetic-primary-container">·</span>{' '}
                info@cloudshuttle.com.au
              </div>
              <div className="mt-3">
                Response target <span className="text-kinetic-primary-container">·</span> within 1
                business day
              </div>
              <div className="mt-2">
                Australia and New Zealand{' '}
                <span className="mx-2 text-kinetic-outline-variant">·</span> remote &amp; on-site
              </div>
            </div>
          }
        />
      ) : (
        <Section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-shuttle-navy/90 to-shuttle-purple/90" />
          <div className="absolute inset-0">
            <div className="absolute inset-0 animate-pulse bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]" />
          </div>
          <Container className="relative z-10 py-24 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="mb-6 flex justify-center">
                <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-r from-shuttle-red to-shuttle-purple shadow-2xl">
                  <Rocket className="h-10 w-10 text-white" />
                </div>
              </div>
              <h1 className="mb-6 bg-gradient-to-r from-white via-shuttle-red to-white bg-clip-text text-6xl font-bold text-transparent md:text-7xl">
                Let&apos;s Connect
              </h1>
              <p className="mx-auto max-w-4xl text-xl leading-relaxed text-gray-200 md:text-2xl">
                Ready to transform your data vision into reality? Let&apos;s start the conversation.
              </p>
            </motion.div>
          </Container>
        </Section>
      )}

      {/* Contact Info Cards */}
      {!isDark && (
        <Section className={isDark ? darkTheme.bg : 'bg-white'}>
          <Container className="py-20">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={containerVariants}
            >
              <motion.div
                className={
                  isDark
                    ? 'border-kinetic-outline-variant/30 mb-12 flex flex-col gap-6 border-b pb-8 md:flex-row md:items-end md:justify-between'
                    : 'mb-16 text-center'
                }
                variants={itemVariants}
              >
                <div>
                  <h2
                    className={cn(
                      'font-headline text-4xl font-bold tracking-tight md:text-5xl',
                      isDark
                        ? 'text-white'
                        : 'bg-gradient-to-r from-shuttle-navy via-shuttle-purple to-shuttle-red bg-clip-text text-transparent'
                    )}
                  >
                    Get In Touch
                  </h2>
                  {isDark ? (
                    <p className="mt-2 font-mono text-xs uppercase tracking-[0.3em] text-kinetic-secondary-fixed-dim">
                      contact_endpoints.yaml
                    </p>
                  ) : (
                    <div className="mx-auto mt-4 h-1 w-24 bg-gradient-to-r from-shuttle-red to-shuttle-purple" />
                  )}
                </div>
                {isDark ? (
                  <div className="hidden text-right font-mono text-[10px] uppercase leading-relaxed text-kinetic-outline md:block">
                    Response: 1 business day
                    <br />
                    Region: AU · NZ
                  </div>
                ) : null}
              </motion.div>

              <motion.div
                className="mb-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4"
                variants={containerVariants}
              >
                {contactInfo.map((info, ci) => {
                  const IconComponent = info.icon
                  return (
                    <motion.div key={info.title} variants={itemVariants}>
                      <Card
                        className={cn(
                          'group h-full transition-all duration-500 magnetic-hover',
                          isDark
                            ? 'bg-kinetic-surface-container-highest/60 hover:border-kinetic-primary-container/30 rounded-none border border-white/5 shadow-none backdrop-blur-[20px]'
                            : 'border-0 bg-white shadow-lg hover:shadow-2xl'
                        )}
                      >
                        <CardContent className="relative p-6 text-center">
                          {isDark ? (
                            <span className="text-kinetic-primary-container/50 absolute right-3 top-3 font-mono text-[10px]">
                              CH_{String(ci + 1).padStart(2, '0')}
                            </span>
                          ) : null}
                          <div
                            className={cn(
                              'mx-auto mb-4 flex h-16 w-16 items-center justify-center shadow-lg transition-transform duration-500 group-hover:scale-110',
                              isDark
                                ? 'rounded-none bg-kinetic-primary-container'
                                : 'rounded-2xl bg-gradient-to-br from-shuttle-red to-shuttle-purple'
                            )}
                          >
                            <IconComponent className="h-8 w-8 text-white" />
                          </div>
                          <h3
                            className={`mb-2 font-headline text-xl font-bold transition-colors duration-300 ${isDark ? darkTheme.text : 'text-shuttle-navy group-hover:text-shuttle-red'}`}
                          >
                            {info.title}
                          </h3>
                          <p
                            className={`mb-1 font-semibold ${isDark ? darkTheme.accent : 'text-shuttle-purple'}`}
                          >
                            {info.details}
                          </p>
                          <p className={`text-sm ${isDark ? darkTheme.textDim : 'text-gray-600'}`}>
                            {info.description}
                          </p>
                          {info.action && (
                            <div className="mt-4">
                              <Button
                                size="default"
                                variant="ghost"
                                className={
                                  isDark
                                    ? 'border-kinetic-primary-container text-kinetic-primary-container hover:bg-kinetic-primary-container hover:text-kinetic-on-primary'
                                    : 'border-shuttle-purple text-shuttle-purple hover:bg-shuttle-purple hover:text-white'
                                }
                                onClick={() => (window.location.href = info.action!)}
                              >
                                Contact
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  )
                })}
              </motion.div>
            </motion.div>
          </Container>
        </Section>
      )}

      {/* Contact Form Section */}
      <Section className={isDark ? darkTheme.bg : 'bg-gradient-to-br from-gray-50 to-white'}>
        <Container className="py-20">
          <div className="mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className={cn('mb-12', !isDark && 'text-center')}
            >
              <div
                className={
                  isDark
                    ? 'border-kinetic-outline-variant/30 flex flex-col gap-4 border-b pb-8 md:flex-row md:items-end md:justify-between'
                    : ''
                }
              >
                <div>
                  <h2
                    className={cn(
                      'font-headline text-4xl font-bold tracking-tight md:text-5xl',
                      isDark
                        ? 'text-white'
                        : 'bg-gradient-to-r from-shuttle-navy via-shuttle-purple to-shuttle-red bg-clip-text text-transparent'
                    )}
                  >
                    {isDark ? 'Send a message' : 'Start Your Project'}
                  </h2>
                  {isDark ? (
                    <p className="mt-2 font-mono text-xs uppercase tracking-[0.3em] text-kinetic-secondary-fixed-dim">
                      contact_form
                    </p>
                  ) : (
                    <p className="mt-4 text-xl text-gray-600">
                      Tell us about your data engineering needs and let&apos;s build something
                      amazing together
                    </p>
                  )}
                </div>
                {isDark ? (
                  <span className="font-mono text-[10px] uppercase tracking-widest text-kinetic-outline md:text-right">
                    Region: AU · NZ
                  </span>
                ) : null}
              </div>
              {isDark ? (
                <p className="mt-6 text-center text-lg text-kinetic-on-surface-variant md:text-left">
                  Share a few details about your data engineering or AI evaluation needs —
                  we&apos;ll route your message to the right person on the team.
                </p>
              ) : null}
            </motion.div>

            {/* Status Messages */}
            {submitStatus.type && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className={cn(
                  'mb-6 border p-6 shadow-lg',
                  isDark ? 'rounded-none' : 'rounded-2xl',
                  submitStatus.type === 'success'
                    ? isDark
                      ? 'bg-green-900/30 border-green-700 text-green-300'
                      : 'border-green-200 bg-green-50 text-green-800'
                    : isDark
                      ? 'bg-red-900/30 border-red-700 text-red-300'
                      : 'border-red-200 bg-red-50 text-red-800'
                )}
              >
                <div className="flex items-center">
                  <div
                    className={`mr-3 h-2 w-2 rounded-full ${
                      submitStatus.type === 'success' ? 'bg-green-500' : 'bg-red-500'
                    }`}
                  />
                  {submitStatus.message}
                </div>
              </motion.div>
            )}

            {/* Form */}
            <div className={cn('relative', isDark && 'pb-1 pt-1')}>
              {isDark ? <CornerBrackets className="z-0" /> : null}
              <motion.form
                onSubmit={handleSubmit}
                className={cn(
                  'relative z-10 space-y-6 p-8 shadow-2xl',
                  isDark
                    ? 'bg-kinetic-surface-container-highest/60 rounded-none border border-white/5 backdrop-blur-[20px]'
                    : 'rounded-2xl border border-gray-100 bg-white'
                )}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {/* Name Field */}
                  <div>
                    <label
                      htmlFor="name"
                      className={cn(
                        'mb-2 block',
                        isDark
                          ? 'flex justify-between font-mono text-[10px] uppercase tracking-widest text-kinetic-outline'
                          : 'font-headline text-lg font-bold text-shuttle-navy'
                      )}
                    >
                      {isDark ? (
                        <>
                          <span>User_Identity</span>
                          <span className="text-kinetic-primary-container/50">REQ*</span>
                        </>
                      ) : (
                        'Full Name'
                      )}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className={cn(
                        'w-full border-2 px-4 py-3 transition-all',
                        isDark ? 'rounded-none' : 'rounded-xl',
                        isDark ? 'font-mono text-sm' : 'text-lg',
                        formErrors.name
                          ? isDark
                            ? darkTheme.inputError
                            : 'focus:ring-red-500/20 border-red-400 focus:border-red-500 focus:ring-2'
                          : isDark
                            ? darkTheme.inputBg
                            : 'border-gray-200 focus:border-shuttle-red focus:ring-2 focus:ring-shuttle-red/20'
                      )}
                      placeholder={isDark ? 'FULL_NAME' : 'John Doe'}
                      required
                      disabled={isSubmitting}
                      aria-invalid={!!formErrors.name}
                      aria-describedby={formErrors.name ? 'name-error' : undefined}
                    />
                    {formErrors.name && (
                      <p
                        id="name-error"
                        className={`mt-1 text-sm ${isDark ? 'text-red-400' : 'text-red-600'} flex items-center`}
                      >
                        <span className="mr-2 h-1 w-1 rounded-full bg-red-600" />
                        {formErrors.name}
                      </p>
                    )}
                  </div>

                  {/* Email Field */}
                  <div>
                    <label
                      htmlFor="email"
                      className={cn(
                        'mb-2 block',
                        isDark
                          ? 'flex justify-between font-mono text-[10px] uppercase tracking-widest text-kinetic-outline'
                          : 'font-headline text-lg font-bold text-shuttle-navy'
                      )}
                    >
                      {isDark ? (
                        <>
                          <span>Return_Address</span>
                          <span className="text-kinetic-primary-container/50">REQ*</span>
                        </>
                      ) : (
                        'Email Address'
                      )}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className={cn(
                        'w-full border-2 px-4 py-3 transition-all',
                        isDark ? 'rounded-none' : 'rounded-xl',
                        isDark ? 'font-mono text-sm' : 'text-lg',
                        formErrors.email
                          ? isDark
                            ? darkTheme.inputError
                            : 'focus:ring-red-500/20 border-red-400 focus:border-red-500 focus:ring-2'
                          : isDark
                            ? darkTheme.inputBg
                            : 'border-gray-200 focus:border-shuttle-red focus:ring-2 focus:ring-shuttle-red/20'
                      )}
                      placeholder={isDark ? 'EMAIL@DOMAIN.COM' : 'john@example.com'}
                      required
                      disabled={isSubmitting}
                      aria-invalid={!!formErrors.email}
                      aria-describedby={formErrors.email ? 'email-error' : undefined}
                    />
                    {formErrors.email && (
                      <p
                        id="email-error"
                        className={`mt-1 text-sm ${isDark ? 'text-red-400' : 'text-red-600'} flex items-center`}
                      >
                        <span className="mr-2 h-1 w-1 rounded-full bg-red-600" />
                        {formErrors.email}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {/* Phone Field */}
                  <div>
                    <label
                      htmlFor="phone"
                      className={cn(
                        'mb-2 block',
                        isDark
                          ? 'flex justify-between font-mono text-[10px] uppercase tracking-widest text-kinetic-outline'
                          : 'font-headline text-lg font-bold text-shuttle-navy'
                      )}
                    >
                      {isDark ? (
                        <>
                          <span>Contact_Number</span>
                          <span className="text-kinetic-primary-container/50">REQ*</span>
                        </>
                      ) : (
                        'Phone Number'
                      )}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className={cn(
                        'w-full border-2 px-4 py-3 transition-all',
                        isDark ? 'rounded-none' : 'rounded-xl',
                        isDark ? 'font-mono text-sm' : 'text-lg',
                        formErrors.phone
                          ? isDark
                            ? darkTheme.inputError
                            : 'focus:ring-red-500/20 border-red-400 focus:border-red-500 focus:ring-2'
                          : isDark
                            ? darkTheme.inputBg
                            : 'border-gray-200 focus:border-shuttle-red focus:ring-2 focus:ring-shuttle-red/20'
                      )}
                      placeholder={isDark ? '+61 433 825 293' : '+61 412 345 678'}
                      required
                      disabled={isSubmitting}
                      aria-invalid={!!formErrors.phone}
                      aria-describedby={formErrors.phone ? 'phone-error' : undefined}
                    />
                    {formErrors.phone && (
                      <p
                        id="phone-error"
                        className={`mt-1 text-sm ${isDark ? 'text-red-400' : 'text-red-600'} flex items-center`}
                      >
                        <span className="mr-2 h-1 w-1 rounded-full bg-red-600" />
                        {formErrors.phone}
                      </p>
                    )}
                  </div>

                  {/* Service Selection */}
                  <div>
                    <label
                      htmlFor="service"
                      className={cn(
                        'mb-2 block',
                        isDark
                          ? 'flex justify-between font-mono text-[10px] uppercase tracking-widest text-kinetic-outline'
                          : 'font-headline text-lg font-bold text-shuttle-navy'
                      )}
                    >
                      {isDark ? (
                        <>
                          <span>Service_Interest</span>
                          <span className="text-kinetic-primary-container/50">REQ*</span>
                        </>
                      ) : (
                        'Service Interest'
                      )}
                    </label>
                    <select
                      id="service"
                      name="service"
                      className={cn(
                        'w-full border-2 px-4 py-3 transition-all',
                        isDark ? 'rounded-none' : 'rounded-xl',
                        isDark ? 'font-mono text-sm' : 'text-lg',
                        formErrors.service
                          ? isDark
                            ? darkTheme.inputError
                            : 'focus:ring-red-500/20 border-red-400 focus:border-red-500 focus:ring-2'
                          : isDark
                            ? darkTheme.inputBg
                            : 'border-gray-200 focus:border-shuttle-red focus:ring-2 focus:ring-shuttle-red/20'
                      )}
                      required
                      disabled={isSubmitting}
                      aria-invalid={!!formErrors.service}
                      aria-describedby={formErrors.service ? 'service-error' : undefined}
                    >
                      <option value="">Select a service</option>
                      <option value="data-ml">Data Engineering & ML</option>
                      <option value="app-dev">Application Development</option>
                      <option value="cloud">Cloud Migrations</option>
                      <option value="conference">Conference & Training</option>
                      <option value="consulting">Strategy Consulting</option>
                    </select>
                    {formErrors.service && (
                      <p
                        id="service-error"
                        className={`mt-1 text-sm ${isDark ? 'text-red-400' : 'text-red-600'} flex items-center`}
                      >
                        <span className="mr-2 h-1 w-1 rounded-full bg-red-600" />
                        {formErrors.service}
                      </p>
                    )}
                  </div>
                </div>

                {/* Description Field */}
                <div>
                  <label
                    htmlFor="description"
                    className={cn(
                      'mb-2 block',
                      isDark
                        ? 'flex justify-between font-mono text-[10px] uppercase tracking-widest text-kinetic-outline'
                        : 'font-headline text-lg font-bold text-shuttle-navy'
                    )}
                  >
                    {isDark ? (
                      <>
                        <span>Project_Description</span>
                        <span className="text-kinetic-primary-container/50">REQ*</span>
                      </>
                    ) : (
                      'Project Description'
                    )}
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={5}
                    className={cn(
                      'w-full resize-none border-2 px-4 py-3 transition-all',
                      isDark ? 'rounded-none' : 'rounded-xl',
                      isDark ? 'font-mono text-sm' : 'text-lg',
                      formErrors.description
                        ? isDark
                          ? darkTheme.inputError
                          : 'focus:ring-red-500/20 border-red-400 focus:border-red-500 focus:ring-2'
                        : isDark
                          ? darkTheme.inputBg
                          : 'border-gray-200 focus:border-shuttle-red focus:ring-2 focus:ring-shuttle-red/20'
                    )}
                    placeholder={
                      isDark
                        ? 'Project goals, timeline, and stack (optional)…'
                        : 'Tell us about your project, goals, and timeline...'
                    }
                    required
                    disabled={isSubmitting}
                    aria-invalid={!!formErrors.description}
                    aria-describedby={formErrors.description ? 'description-error' : undefined}
                  />
                  {formErrors.description && (
                    <p
                      id="description-error"
                      className={`mt-1 text-sm ${isDark ? 'text-red-400' : 'text-red-600'} flex items-center`}
                    >
                      <span className="mr-2 h-1 w-1 rounded-full bg-red-600" />
                      {formErrors.description}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  size="xl"
                  className={cn(
                    'w-full px-8 py-4 text-xl font-bold text-white shadow-lg transition-all duration-300',
                    isDark ? 'rounded-none' : 'rounded-xl',
                    isSubmitting && 'cursor-not-allowed opacity-75',
                    isDark
                      ? 'bg-kinetic-primary-container text-kinetic-on-primary hover:shadow-[0_0_24px_rgba(0,240,255,0.35)] hover:brightness-110'
                      : 'bg-gradient-to-r from-shuttle-red to-shuttle-purple hover:from-shuttle-red/90 hover:to-shuttle-purple/90 hover:shadow-2xl'
                  )}
                >
                  <span>
                    {isSubmitting
                      ? isDark
                        ? 'Sending…'
                        : 'Sending...'
                      : isDark
                        ? 'Send message'
                        : 'Send Message'}
                  </span>
                  {isSubmitting ? (
                    <Loader2 className="ml-2 h-6 w-6 animate-spin" />
                  ) : (
                    <Send className="ml-2 h-6 w-6" />
                  )}
                </Button>
              </motion.form>
            </div>
          </div>
        </Container>
      </Section>

      {/* Additional Information Section */}
      {!isDark && (
        <Section
          className={
            isDark
              ? 'border-kinetic-outline-variant/30 border-t bg-kinetic-surface-container-low'
              : 'bg-gradient-to-r from-shuttle-navy via-shuttle-purple to-shuttle-navy'
          }
        >
          <Container className="py-20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h2
                className={`mb-6 text-4xl font-bold md:text-5xl ${isDark ? darkTheme.text : 'text-white'}`}
              >
                Why Choose Cloud Shuttle?
              </h2>
              <div className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-8 md:grid-cols-3">
                <div className="text-center">
                  <div
                    className={cn(
                      'mx-auto mb-4 flex h-16 w-16 items-center justify-center',
                      isDark
                        ? 'rounded-none bg-kinetic-surface-container-highest'
                        : 'rounded-2xl bg-white/10'
                    )}
                  >
                    <Users className="h-8 w-8 text-white" />
                  </div>
                  <h3
                    className={`mb-2 font-headline text-xl font-bold ${isDark ? darkTheme.text : 'text-white'}`}
                  >
                    Expert Team
                  </h3>
                  <p className={isDark ? darkTheme.textMuted : 'text-gray-200'}>
                    Open source practitioners and data engineering specialists
                  </p>
                </div>
                <div className="text-center">
                  <div
                    className={cn(
                      'mx-auto mb-4 flex h-16 w-16 items-center justify-center',
                      isDark
                        ? 'rounded-none bg-kinetic-surface-container-highest'
                        : 'rounded-2xl bg-white/10'
                    )}
                  >
                    <Globe className="h-8 w-8 text-white" />
                  </div>
                  <h3
                    className={`mb-2 font-headline text-xl font-bold ${isDark ? darkTheme.text : 'text-white'}`}
                  >
                    Global Reach
                  </h3>
                  <p className={isDark ? darkTheme.textMuted : 'text-gray-200'}>
                    Serving clients across Australia and New Zealand
                  </p>
                </div>
                <div className="text-center">
                  <div
                    className={cn(
                      'mx-auto mb-4 flex h-16 w-16 items-center justify-center',
                      isDark
                        ? 'rounded-none bg-kinetic-surface-container-highest'
                        : 'rounded-2xl bg-white/10'
                    )}
                  >
                    <Rocket className="h-8 w-8 text-white" />
                  </div>
                  <h3
                    className={`mb-2 font-headline text-xl font-bold ${isDark ? darkTheme.text : 'text-white'}`}
                  >
                    Rapid Delivery
                  </h3>
                  <p className={isDark ? darkTheme.textMuted : 'text-gray-200'}>
                    Fast project delivery with superior agility
                  </p>
                </div>
              </div>
            </motion.div>
          </Container>
        </Section>
      )}

      {isDark && (
        <footer className="border-kinetic-outline-variant/30 border-t bg-kinetic-surface py-8">
          <Container>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <span className="font-headline text-base font-bold text-white">Cloud Shuttle</span>
                <p className="mt-1 text-sm text-kinetic-on-surface-variant">
                  Empowering the next generation of data professionals.
                </p>
              </div>
              <div className="flex gap-6">
                <Link
                  href="/about-us"
                  className="text-sm text-kinetic-on-surface-variant hover:text-kinetic-on-surface"
                >
                  About
                </Link>
                <Link
                  href="/workshops"
                  className="text-sm text-kinetic-on-surface-variant hover:text-kinetic-on-surface"
                >
                  Workshops
                </Link>
                <Link
                  href="/services"
                  className="text-sm text-kinetic-on-surface-variant hover:text-kinetic-on-surface"
                >
                  Services
                </Link>
              </div>
            </div>
            <p className="mt-4 font-mono text-[10px] text-sm uppercase tracking-wider text-kinetic-on-surface-variant">
              © {new Date().getFullYear()} Cloud Shuttle. All rights reserved.
            </p>
          </Container>
        </footer>
      )}
    </div>
  )
}
